import { DIRS, hexDist, hexLen, hexToWorld, type Hex } from '../hex.ts';
import { imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';
import type { Knot } from '../aster-types.ts';

/**
 * The ranger's path, chapter 1 (docs/sendas/explorador.md, «Las piedras de Aster»): its grounds from the seed, the same
 * in the sim and the drawing, integers only. Up is −y (the top of the screen); the sun is to the west (−x), the sea to
 * the east (+x): a knot's side is which of the two halves its cairn stands in, seen from the cairn read.
 *
 * - `cairnsLayout` (act 1, the knots, small): the rim of the Furrows, live stone and moss, two Furrows cut across it
 *   (rock, with gaps to cross); seven or eight cairns. Aster's first by the foot; its cord names the second (rings and
 *   a side), whose cord names the third (rings, a side and the tight knot: on a high plate). Checked on paper: from the
 *   first, one cairn at its rings on its side; from the second, two at its rings on its side and only one on a plate.
 *   The obvious ones are there too: one at the rings on the other side, one a ring short on the right side.
 * - `echoLayout` (act 2, the fog): a Furrow from the foot to the top, three forks on the way, each a chamber with three
 *   or four mouths; one goes on, the rest end two to four hexes in. The widest is always a dead end, and the dead ends
 *   of a fork end at different depths (the further, the later its echo).
 * - `fallenLayout` (act 3, the test): a slope of loose plates; five cairns standing, one with the long cord (rings from
 *   it, the westmost or eastmost of the others, rings from that one); four light bases on the slope where cairns
 *   fell. Exactly one base lies on both rings; one lies on the first only, one on the second only, and the nearest to
 *   the long cord's cairn (the obvious) is a ring short.
 */

export const CAIRN_RADIUS = 9;
export const ECHO_RADIUS = 10;
export const FALLEN_RADIUS = 10;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const add = (a: Hex, d: Hex, k = 1): Hex => ({ q: a.q + d.q * k, r: a.r + d.r * k });
/** World x of a hex (thousandths of a hex; a half hex is 500). */
const wx = (h: Hex): number => hexToWorld(h.q, h.r).x;

/** The side of `b` seen from `a`: −1 the sun's (west), +1 the sea's (east), 0 straight up or down (never a knot's). */
export const knotSide = (a: Hex, b: Hex): number => {
  const d = wx(b) - wx(a);
  return d < 0 ? -1 : d > 0 ? 1 : 0;
};

/** Whether `c` answers a knot read off `from`. */
export const knotFits = (from: Hex, c: Hex, plate: boolean, k: Knot): boolean => hexDist(from, c) === k.rings && knotSide(from, c) === k.side && (!k.tight || plate);

/* ---------- act 1: the rim of the Furrows ---------- */

export interface CairnLayout {
  rocks: Hex[];
  /** The Furrows' cells (the rocks, drawn as grooves), and the high plates (a cairn stands on some). */
  furrows: Hex[];
  cairns: Hex[];
  plates: boolean[];
  /** Aster's way (indexes into `cairns`) and the cords of its first two. */
  way: number[];
  knots: Knot[];
  /** The obvious wrong ones: for the first knot (the other side, a ring short), for the second (its twin off the plate). */
  decoys: number[];
  rim: Hex[];
  start: { x: number; y: number };
}

/** Two Furrows across the rim, east to west, each with two gaps two hexes wide. */
const furrowsOf = (rng: RngState, R: number): Hex[] => {
  const out: Hex[] = [];
  for (const r0 of [rngRange(rng, 0, 1), rngRange(rng, -5, -4)]) {
    const gaps = [rngRange(rng, -6, -2), rngRange(rng, 1, 5)];
    for (let q = -R; q <= R; q++) {
      const h = { q: q - (r0 >> 1), r: r0 };
      if (hexLen(h.q, h.r) > R) continue;
      const x = wx(h);
      if (gaps.some((g) => x >= g * 1000 - 600 && x <= g * 1000 + 1600)) continue;
      out.push(h);
    }
  }
  return out;
};

export const cairnsLayout = (seed: number): CairnLayout => {
  const R = CAIRN_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xca1e));
  for (let attempt = 0; attempt < 600; attempt++) {
    const furrows = furrowsOf(rng, R);
    const rock = new Set(furrows.map(key));
    // Cairns stand off the board's edge and off the Furrows' lips.
    const open = allCells(R - 1).filter((h) => !rock.has(key(h)) && !DIRS.some((d) => rock.has(key(add(h, d)))));
    const pick = (f: (h: Hex) => boolean): Hex | null => {
      const c = open.filter(f);
      return c.length ? (c[rngInt(rng, c.length)] as Hex) : null;
    };
    const A = pick((h) => h.r >= 1 && h.r <= 6);
    if (!A) continue;
    const k1: Knot = { rings: rngRange(rng, 3, 4), side: rngInt(rng, 2) ? 1 : -1, tight: false };
    const B = pick((h) => hexDist(A, h) === k1.rings && knotSide(A, h) === k1.side);
    if (!B) continue;
    const k2: Knot = { rings: rngRange(rng, 3, 5), side: rngInt(rng, 2) ? 1 : -1, tight: true };
    const far = (h: Hex, list: Hex[]): boolean => list.every((o) => hexDist(o, h) >= 2);
    const C = pick((h) => hexDist(B, h) === k2.rings && knotSide(B, h) === k2.side && far(h, [A, B]));
    if (!C) continue;
    const D = pick((h) => hexDist(B, h) === k2.rings && knotSide(B, h) === k2.side && far(h, [A, B, C]));
    if (!D) continue;
    const X1 = pick((h) => hexDist(A, h) === k1.rings && knotSide(A, h) === -k1.side && far(h, [A, B, C, D]));
    if (!X1) continue;
    const X2 = pick((h) => hexDist(A, h) === k1.rings - 1 && knotSide(A, h) === k1.side && far(h, [A, B, C, D, X1]));
    if (!X2) continue;
    const Y1 = pick((h) => hexDist(B, h) === k2.rings && knotSide(B, h) === -k2.side && far(h, [A, B, C, D, X1, X2]));
    if (!Y1) continue;
    const list = [A, B, C, D, X1, X2, Y1];
    if (rngInt(rng, 2)) {
      const Z = pick((h) => far(h, list) && hexDist(A, h) !== k1.rings && hexDist(B, h) !== k2.rings);
      if (Z) list.push(Z);
    }
    // In board order, so an index says nothing of the way.
    const cairns = list.slice().sort(sortHex);
    const ix = (h: Hex): number => cairns.findIndex((c) => c.q === h.q && c.r === h.r);
    // The third on a plate; a wrong one on another (a plate alone says nothing).
    const plates = cairns.map((c) => c === C || c === X1);
    const way = [ix(A), ix(B), ix(C)];
    const ok = ((): boolean => {
      // The first knot: exactly one cairn at its rings on its side (no plate twin: the knot isn't tight).
      const m1 = cairns.filter((c, i) => i !== way[0] && hexDist(A, c) === k1.rings && knotSide(A, c) === k1.side);
      if (m1.length !== 1 || m1[0] !== B) return false;
      // The second: two at its rings on its side, only one on a plate.
      const m2 = cairns.filter((c, i) => i !== way[0] && i !== way[1] && hexDist(B, c) === k2.rings && knotSide(B, c) === k2.side);
      if (m2.length !== 2) return false;
      const fit = cairns.filter((c, i) => i !== way[0] && i !== way[1] && knotFits(B, c, plates[i] as boolean, k2));
      if (fit.length !== 1 || fit[0] !== C) return false;
      // Nothing straight up or down a ring away (its side would be neither).
      for (const c of cairns) {
        if (hexDist(A, c) === k1.rings && knotSide(A, c) === 0) return false;
        if (hexDist(B, c) === k2.rings && knotSide(B, c) === 0) return false;
      }
      return true;
    })();
    if (!ok) continue;
    const rocks = furrows.slice().sort(sortHex);
    const rim = allCells(R)
      .filter((h) => hexLen(h.q, h.r) === R && !rock.has(key(h)) && h.r < 2)
      .sort(sortHex);
    // The hero by the first cairn, a step down and aside.
    const st = open.find((h) => hexDist(A, h) === 2 && h.r > A.r) ?? add(A, DIRS[5] as Hex, 2);
    return {
      rocks,
      furrows: rocks,
      cairns,
      plates,
      way,
      knots: [k1, k2],
      decoys: [ix(X1), ix(X2), ix(D)],
      rim,
      start: hexToWorld(st.q, st.r),
    };
  }
  throw new Error(`cairnsLayout: no rim for seed ${seed}`);
};

/* ---------- act 2: the fog in the Furrow ---------- */

export interface EchoMouthLayout {
  dir: number;
  cells: Hex[];
  open: boolean;
  wide: boolean;
}

export interface EchoForkLayout {
  at: Hex;
  mouths: EchoMouthLayout[];
}

export interface EchoLayout {
  rocks: Hex[];
  floor: Hex[];
  forks: EchoForkLayout[];
  /** The way in, from the foot to the first fork, and the way out, from the last to the top. */
  start: Hex;
  exit: Hex;
}

/** The north-going ways (NE, NW) and the mouths' other directions (E, W, and the other north one). */
const NORTH = [1, 2];

const shuffled = <T>(rng: RngState, list: T[]): T[] => {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = out[i] as T;
    out[i] = out[j] as T;
    out[j] = t;
  }
  return out;
};

export const echoLayout = (seed: number): EchoLayout => {
  const R = ECHO_RADIUS;
  for (let salt = 0; salt < 64; salt++) {
    const rng = seedRng(deriveSeed(seed, 0xec40 + salt));
    for (let attempt = 0; attempt < 60; attempt++) {
      const got = echoTry(rng, R);
      if (got) return got;
    }
  }
  throw new Error(`echoLayout: no Furrow for seed ${seed}`);
};

const echoTry = (rng: RngState, R: number): EchoLayout | null => {
  // Which branch each floor cell is of (−1 a fork's chamber, 1 the way, 2 a dead end), and of which fork.
  const floor = new Map<string, { b: number; f: number }>();
  const start: Hex = { q: rngRange(rng, -6, -2), r: 8 };
  // The way: from the start north to the first fork, then fork to fork, then out the top; it leans back to the middle.
  const forks: EchoForkLayout[] = [];
  let at = start;
  const way: Hex[] = [at];
  for (let i = 0; i < 4; i++) {
    const x = wx(at);
    const dir = x > 1500 ? 2 : x < -1500 ? 1 : (NORTH[rngInt(rng, 2)] as number);
    const n = i === 0 ? 3 : rngRange(rng, 4, 5);
    for (let k = 1; k <= n; k++) {
      at = add(at, DIRS[dir] as Hex);
      way.push(at);
    }
    if (hexLen(at.q, at.r) > R - 1) return null;
    if (i < 3) forks.push({ at, mouths: [] });
  }
  const exit = way[way.length - 1] as Hex;
  if (exit.r > -7) return null;
  forks.forEach((f, fi) => {
    floor.set(key(f.at), { b: -1, f: fi });
    for (const d of DIRS) floor.set(key(add(f.at, d)), { b: -1, f: fi });
  });
  for (const h of way) if (!floor.has(key(h))) floor.set(key(h), { b: 1, f: -1 });
  // Each fork's mouths: the way on, then two or three dead ends where they fit (none opening on another branch).
  for (let fi = 0; fi < forks.length; fi++) {
    const f = forks[fi] as EchoForkLayout;
    const i = way.findIndex((h) => h.q === f.at.q && h.r === f.at.r);
    const nx = way[i + 1] as Hex;
    const onDir = DIRS.findIndex((d) => d.q === nx.q - f.at.q && d.r === nx.r - f.at.r);
    const on: EchoMouthLayout = { dir: onDir, cells: [], open: true, wide: false };
    for (let k = i + 2; k < way.length; k++) {
      const h = way[k] as Hex;
      on.cells.push(h);
      if (forks.some((g) => g.at.q === h.q && g.at.r === h.r)) break;
    }
    f.mouths.push(on);
    const want = rngRange(rng, 2, 3);
    const lens = shuffled(rng, [2, 3, 4]);
    let wide = false;
    for (const dir of shuffled(rng, [0, 3, ...NORTH].filter((d) => d !== onDir))) {
      if (f.mouths.length > want) break;
      const len = lens[f.mouths.length - 1] as number;
      const cells: Hex[] = [];
      const w = !wide;
      for (let k = 2; k <= 1 + len; k++) {
        const c = add(f.at, DIRS[dir] as Hex, k);
        cells.push(c);
        if (w) cells.push(add(c, DIRS[(dir + 1) % 6] as Hex));
      }
      if (w) cells.push(add(add(f.at, DIRS[dir] as Hex), DIRS[(dir + 1) % 6] as Hex));
      const mine = new Set(cells.map(key));
      const fits = cells.every((c) => {
        if (hexLen(c.q, c.r) > R - 1) return false;
        const o = floor.get(key(c));
        if (o && !(o.b === -1 && o.f === fi)) return false;
        if (forks.some((g, gi) => gi !== fi && hexDist(c, g.at) <= 2)) return false;
        return DIRS.every((d) => {
          const n = add(c, d);
          if (mine.has(key(n))) return true;
          const x = floor.get(key(n));
          return !x || (x.b === -1 && x.f === fi);
        });
      });
      if (!fits) continue;
      for (const c of cells) if (!floor.has(key(c))) floor.set(key(c), { b: 2, f: fi });
      f.mouths.push({ dir, cells: cells.filter((c) => !(floor.get(key(c))?.b === -1)), open: false, wide: w });
      wide = true;
    }
    if (f.mouths.length < 3) return null;
    // Dead ends of a fork at different depths: their echoes come back apart.
    const depths = f.mouths.filter((m) => !m.open).map((m) => mouthDepth(f, m));
    if (new Set(depths).size !== depths.length) return null;
  }
  // Mouths in a fixed order round the chamber (by direction), so the way on isn't always the first.
  for (const f of forks) f.mouths.sort((a, b) => a.dir - b.dir);
  const fl = [...floor.keys()].map((k) => {
    const [q, r] = k.split(',').map(Number) as [number, number];
    return { q, r };
  });
  const set = new Set(floor.keys());
  const rocks = allCells(R).filter((h) => !set.has(key(h)));
  return { rocks: rocks.sort(sortHex), floor: fl.sort(sortHex), forks, start, exit };
};

/** A dead end's depth: hexes from its fork's middle to its wall. */
export const mouthDepth = (f: EchoForkLayout, m: EchoMouthLayout): number => {
  let d = 0;
  for (const c of m.cells) d = imax(d, hexDist(f.at, c));
  return d;
};

/* ---------- act 3: the fallen cairn ---------- */

export interface FallenLayout {
  rocks: Hex[];
  /** Loose plates lying about (for the drawing only). */
  plates: Hex[];
  cairns: Hex[];
  long: number;
  cord: { rings: number; side: number; then: number };
  named: number;
  bases: Hex[];
  truth: number;
  obvious: number;
  /** The base on the long cord's rings only, and the one on the named cairn's only. */
  firstOnly: number;
  secondOnly: number;
  rim: Hex[];
  start: { x: number; y: number };
  from: { x: number; y: number };
}

export const fallenLayout = (seed: number): FallenLayout => {
  const R = FALLEN_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xfa11));
  for (let attempt = 0; attempt < 1500; attempt++) {
    // Slabs of the shell's plates standing up out of the slope: a few clusters of rock.
    const rocks: Hex[] = [];
    for (let i = 0; i < 5; i++) {
      const c = { q: rngRange(rng, -7, 7), r: rngRange(rng, -6, 6) };
      if (hexLen(c.q, c.r) > R - 2) continue;
      rocks.push(c);
      const d = DIRS[rngInt(rng, 6)] as Hex;
      if (rngInt(rng, 2)) rocks.push(add(c, d));
    }
    const rock = new Set(rocks.map(key));
    const open = allCells(R - 1).filter((h) => !rock.has(key(h)) && !DIRS.some((d) => rock.has(key(add(h, d)))));
    const pick = (f: (h: Hex) => boolean): Hex | null => {
      const c = open.filter(f);
      return c.length ? (c[rngInt(rng, c.length)] as Hex) : null;
    };
    const far = (h: Hex, list: Hex[]): boolean => list.every((o) => hexDist(o, h) >= 2);
    const A = pick((h) => h.r >= 1 && h.r <= 4 && Math.abs(wx(h)) <= 3000);
    if (!A) continue;
    const cord = { rings: rngRange(rng, 3, 5), side: rngInt(rng, 2) ? 1 : -1, then: rngRange(rng, 2, 4) };
    // The named one: well out on its side; the other extreme on the other side.
    const B = pick((h) => knotSide(A, h) === cord.side && Math.abs(wx(h) - wx(A)) >= 3000 && far(h, [A]));
    if (!B) continue;
    const B2 = pick((h) => knotSide(A, h) === -cord.side && Math.abs(wx(h) - wx(A)) >= 2000 && far(h, [A, B]));
    if (!B2) continue;
    const T = pick((h) => hexDist(A, h) === cord.rings && hexDist(B, h) === cord.then && far(h, [A, B, B2]));
    if (!T) continue;
    const D1 = pick((h) => hexDist(A, h) === cord.rings - 1 && hexDist(B, h) !== cord.then && far(h, [A, B, B2, T]));
    if (!D1) continue;
    const D2 = pick((h) => hexDist(A, h) === cord.rings && hexDist(B, h) !== cord.then && far(h, [A, B, B2, T, D1]));
    if (!D2) continue;
    const D3 = pick((h) => hexDist(B, h) === cord.then && hexDist(A, h) > cord.rings && far(h, [A, B, B2, T, D1, D2]));
    if (!D3) continue;
    const used = [A, B, B2, T, D1, D2, D3];
    // Two more standing (between the extremes), for the slope's look and the Roller's stops.
    const inner = (h: Hex): boolean => (cord.side > 0 ? wx(h) < wx(B) && wx(h) > wx(B2) : wx(h) > wx(B) && wx(h) < wx(B2));
    const C1 = pick((h) => inner(h) && far(h, used) && hexDist(A, h) >= 3);
    if (!C1) continue;
    const C2 = pick((h) => inner(h) && far(h, [...used, C1]) && hexDist(A, h) >= 3);
    if (!C2) continue;
    const cairnsIn = [A, B, B2, C1, C2];
    const basesIn = [T, D1, D2, D3];
    // The named cairn is the only one that far out on its side.
    const xs = cairnsIn.filter((c) => c !== A).map(wx);
    const ext = xs.reduce((a, x) => (cord.side > 0 ? imax(a, x) : imin(a, x)), xs[0] as number);
    if (xs.filter((x) => x === ext).length !== 1 || wx(B) !== ext) continue;
    // Exactly one base on both rings; the nearest base to the long cord's cairn is the obvious one, alone that near.
    if (basesIn.filter((b) => hexDist(A, b) === cord.rings && hexDist(B, b) === cord.then).length !== 1) continue;
    const near = basesIn.reduce((a, b) => imin(a, hexDist(A, b)), 0x7fffffff);
    if (basesIn.filter((b) => hexDist(A, b) === near).length !== 1 || hexDist(A, D1) !== near) continue;
    // A base under a cairn's foot reads badly: two hexes apart, all of them.
    const all = [...cairnsIn, ...basesIn];
    if (all.some((a, i) => all.some((b, j) => j > i && hexDist(a, b) < 2))) continue;
    const cairns = cairnsIn.slice().sort(sortHex);
    const bases = basesIn.slice().sort(sortHex);
    const ci = (h: Hex): number => cairns.findIndex((c) => c.q === h.q && c.r === h.r);
    const bi = (h: Hex): number => bases.findIndex((c) => c.q === h.q && c.r === h.r);
    // Loose plates on the slope, off the cairns and bases.
    const taken = new Set(all.map(key));
    const plates = open.filter((h) => !taken.has(key(h)) && (h.q * 7 + h.r * 13 + seed) % 5 === 0).sort(sortHex);
    const rim = allCells(R)
      .filter((h) => hexLen(h.q, h.r) === R && h.r <= -3)
      .sort(sortHex);
    const st = open.find((h) => hexDist(A, h) === 2 && h.r > A.r && !taken.has(key(h))) ?? add(A, DIRS[5] as Hex, 2);
    return {
      rocks: rocks.slice().sort(sortHex),
      plates,
      cairns,
      long: ci(A),
      cord,
      named: ci(B),
      bases,
      truth: bi(T),
      obvious: bi(D1),
      firstOnly: bi(D2),
      secondOnly: bi(D3),
      rim,
      start: hexToWorld(st.q, st.r),
      from: hexToWorld(0, -(R - 1)),
    };
  }
  throw new Error(`fallenLayout: no slope for seed ${seed}`);
};
